// components/common/ToastNotification.tsx
//
// Sistema simple de notificaciones tipo "toast" para reemplazar el uso de
// window.alert(). Se muestran ancladas a la esquina superior derecha de la
// pantalla, se autodestruyen a los 5 segundos y se pueden apilar si se
// disparan varias seguidas.
//
// Uso:
//   1) Envolver la app (o la sección donde se necesite) con <ToastProvider>.
//   2) Dentro de cualquier componente hijo: const { showToast } = useToast();
//      showToast({ type: 'success', message: '¡Reserva confirmada!' });

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastOptions {
    type?: ToastType;
    title?: string;
    message: string;
    /** Duración en ms. Por defecto 5000 (5 segundos). */
    duration?: number;
}

interface ToastItem extends Required<Omit<ToastOptions, 'title'>> {
    id: number;
    title?: string;
}

interface ToastContextValue {
    showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_DURATION = 5000;

const estilosPorTipo: Record<
    ToastType,
    { icon: React.ReactNode; barColor: string; iconColor: string }
> = {
    success: {
        icon: <CheckCircle2 className="w-5 h-5" strokeWidth={2.2} />,
        barColor: 'from-emerald-500 to-emerald-400',
        iconColor: 'text-emerald-600',
    },
    error: {
        icon: <XCircle className="w-5 h-5" strokeWidth={2.2} />,
        barColor: 'from-red-500 to-red-400',
        iconColor: 'text-red-600',
    },
    info: {
        icon: <Info className="w-5 h-5" strokeWidth={2.2} />,
        barColor: 'from-rose-600 to-pink-500',
        iconColor: 'text-rose-600',
    },
};

const ToastCard: React.FC<{ toast: ToastItem; onClose: (id: number) => void }> = ({
    toast,
    onClose,
}) => {
    const [saliendo, setSaliendo] = useState(false);
    const estilo = estilosPorTipo[toast.type];

    const cerrar = useCallback(() => {
        setSaliendo(true);
        // Esperar a que termine la animación de salida antes de desmontar
        window.setTimeout(() => onClose(toast.id), 200);
    }, [onClose, toast.id]);

    useEffect(() => {
        const timer = window.setTimeout(cerrar, toast.duration);
        return () => window.clearTimeout(timer);
    }, [cerrar, toast.duration]);

    return (
        <div
            role="status"
            className={`pointer-events-auto relative w-[320px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl bg-white shadow-[0_12px_32px_rgba(20,10,30,0.14)] border border-slate-100 transition-all duration-200 ease-out ${
                saliendo
                    ? 'opacity-0 translate-x-4'
                    : 'opacity-100 translate-x-0 animate-[toast-in_0.25s_ease-out]'
            }`}
        >
            <div className={`h-1 w-full bg-gradient-to-r ${estilo.barColor}`} />
            <div className="flex items-start gap-2.5 px-4 py-3.5">
                <div className={`mt-0.5 flex-shrink-0 ${estilo.iconColor}`}>{estilo.icon}</div>
                <div className="flex-1 min-w-0">
                    {toast.title && (
                        <p className="text-[13.5px] font-bold text-slate-900 leading-snug">
                            {toast.title}
                        </p>
                    )}
                    <p className="text-[13px] text-slate-600 leading-snug break-words">
                        {toast.message}
                    </p>
                </div>
                <button
                    onClick={cerrar}
                    aria-label="Cerrar notificación"
                    className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center text-slate-300 hover:text-slate-500 hover:bg-slate-50 transition-colors"
                >
                    <X className="w-3.5 h-3.5" strokeWidth={2.2} />
                </button>
            </div>
        </div>
    );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const idRef = useRef(0);

    const removeToast = useCallback((id: number) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const showToast = useCallback((options: ToastOptions) => {
        idRef.current += 1;
        const nuevo: ToastItem = {
            id: idRef.current,
            type: options.type ?? 'info',
            title: options.title,
            message: options.message,
            duration: options.duration ?? DEFAULT_DURATION,
        };
        setToasts((prev) => [...prev, nuevo]);
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div
                className="fixed top-5 right-5 z-[100] flex flex-col gap-2.5 pointer-events-none"
                aria-live="polite"
            >
                {toasts.map((toast) => (
                    <ToastCard key={toast.id} toast={toast} onClose={removeToast} />
                ))}
            </div>
            <style>{`
                @keyframes toast-in {
                    from { opacity: 0; transform: translateX(16px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>
        </ToastContext.Provider>
    );
};

export const useToast = (): ToastContextValue => {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast debe usarse dentro de un <ToastProvider>.');
    }
    return ctx;
};